import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface Slide {
  id: string;
  title: string;
  type: string;
  meta: string;
  description: string;
  image: string;
}

const slides: Slide[] = [
  {
    id: "m1",
    title: "星际远征：破晓",
    type: "科幻巨制",
    meta: "美国 · 2025 · 评分 9.1",
    description:
      "人类最后的远航舰队驶向未知星系，一场跨越光年的救援任务，将一群宇航员的命运推向宇宙的尽头。",
    image:
      "https://readdy.ai/api/search-image?query=epic%20sci-fi%20cinematic%20scene%20of%20a%20lone%20spaceship%20approaching%20a%20colossal%20glowing%20planet%2C%20deep%20space%20nebula%20with%20teal%20and%20orange%20glow%2C%20vast%20dark%20atmosphere%20with%20stars%2C%20dramatic%20lighting%20and%20film%20grain%2C%20wide%20cinematic%20composition&width=1600&height=900&seq=cherry-hero-01&orientation=landscape",
  },
  {
    id: "t1",
    title: "都市浮生记",
    type: "都市群像",
    meta: "中国大陆 · 2024 · 评分 8.6",
    description:
      "五个从未谋面的年轻人因一间合租公寓走到一起，在繁华都市里各自奔跑，也彼此照亮。",
    image:
      "https://readdy.ai/api/search-image?query=modern%20urban%20cinematic%20scene%20of%20a%20glowing%20city%20skyline%20at%20dusk%20with%20warm%20window%20lights%2C%20young%20professionals%20silhouetted%20on%20a%20rooftop%2C%20amber%20and%20deep%20charcoal%20tones%2C%20filmic%20bokeh%20and%20soft%20haze%2C%20wide%20composition&width=1600&height=900&seq=cherry-hero-02&orientation=landscape",
  },
  {
    id: "v1",
    title: "星光大擂台",
    type: "舞台竞演",
    meta: "中国大陆 · 2025 · 评分 8.5",
    description:
      "来自全国的舞台新星同台竞技，用歌声与舞蹈争夺年度总冠军，每一期都是一场视听盛宴。",
    image:
      "https://readdy.ai/api/search-image?query=dazzling%20concert%20stage%20cinematic%20scene%20with%20colorful%20spotlights%20and%20confetti%2C%20energetic%20performer%20silhouettes%20under%20bright%20beams%2C%20electric%20crimson%20and%20gold%20tones%2C%20festive%20celebratory%20energy%2C%20wide%20dramatic%20composition&width=1600&height=900&seq=cherry-hero-03&orientation=landscape",
  },
  {
    id: "m3",
    title: "长安夜行录",
    type: "古装悬疑",
    meta: "中国大陆 · 2023 · 评分 8.7",
    description:
      "大唐长安的最后一个夜晚，一名女捕快与神秘刺客被迫联手，揭开一场关乎天下安危的惊天阴谋。",
    image:
      "https://readdy.ai/api/search-image?query=ancient%20chinese%20city%20at%20night%20with%20glowing%20red%20lanterns%20and%20traditional%20architecture%2C%20lone%20warrior%20in%20flowing%20robes%20walking%20down%20a%20misty%20street%2C%20warm%20gold%20and%20deep%20crimson%20palette%2C%20ink%20wash%20texture%2C%20cinematic%20wide%20shot&width=1600&height=900&seq=cherry-hero-04&orientation=landscape",
  },
];

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((v) => (v + 1) % slides.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, []);

  const go = (next: number) => setIndex((next + slides.length) % slides.length);

  return (
    <section className="relative w-full h-[460px] md:h-[600px] overflow-hidden bg-background-950">
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 剧照`}
            title={`${slide.title} ${slide.type}`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950/90 via-background-950/60 to-background-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-transparent to-background-950/30"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-6 flex flex-col justify-end pb-14 md:pb-20">
        <div key={index} className="cherry-fade-up max-w-2xl flex flex-col gap-3 md:gap-4">
          <span className="w-fit px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-bold tracking-wide">
            {slides[index].type}
          </span>
          <h1 className="font-heading font-black text-3xl md:text-5xl text-foreground-950 leading-tight">
            {slides[index].title}
          </h1>
          <p className="text-sm md:text-base text-foreground-600">{slides[index].meta}</p>
          <p className="hidden sm:block text-sm md:text-base text-foreground-700 leading-relaxed max-w-xl">
            {slides[index].description}
          </p>
          <div className="flex items-center gap-3 mt-2">
            <Link
              to={`/detail/${slides[index].id}`}
              className="flex items-center gap-2 px-5 md:px-6 h-11 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-bold whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-play-fill text-lg"></i>
              立即观看
            </Link>
            <Link
              to={`/detail/${slides[index].id}`}
              className="flex items-center gap-2 px-5 md:px-6 h-11 rounded-md bg-foreground-950/10 hover:bg-foreground-950/20 border border-foreground-800/30 text-foreground-900 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-information-line text-base"></i>
              查看详情
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-6 md:mt-8">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => go(index - 1)}
            className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-foreground-950/10 hover:bg-primary-500 text-foreground-900 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <div className="flex items-center gap-2">
            {slides.map((slide, i) => (
              <button
                key={slide.id}
                type="button"
                aria-label={`切换到第 ${i + 1} 张`}
                onClick={() => go(i)}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  i === index ? "w-8 bg-primary-500" : "w-4 bg-foreground-800/40 hover:bg-foreground-700"
                }`}
              ></button>
            ))}
          </div>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => go(index + 1)}
            className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-foreground-950/10 hover:bg-primary-500 text-foreground-900 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>
      </div>
    </section>
  );
}